import {useCallback, useEffect, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {merchantService} from '@/services/merchants';
import type {AfriexQueryParams} from '@/types';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import {logRejection} from '@/utils/logRejection';

const useMerchantList = () => {
  const [businessList, setBusinessList] = useState<
    {
      business: AfriexBusiness;
      person: AfriexBusinessUser;
    }[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch] = useState<boolean>(false);
  const [userTotal, setUserTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchAllBusinesses = useCallback(
    async (params?: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data, total} = await merchantService.getBusinesses({
          ...params,
          ...currentFilters,
        });
        setBusinessList(data);
        setUserTotal(total || 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  const handleBusinessAutoComplete = useCallback(
    async (searchTerm: string) => {
      try {
        setError(null);
        setIsLoading(true);
        setShowPagination(false);

        if (!searchTerm?.trim() || searchTerm.length < 3) {
          await fetchAllBusinesses({});
          return;
        }

        if (searchTerm.includes('@')) {
          // Route to business user endpoint instead (since it returns business, person)
          const list = await merchantService.searchUserAutoComplete(
            searchTerm?.toLowerCase(),
          );
          if (list && list?.length > 0) {
            setBusinessList(list);
            setUserTotal(1);
          }
          return;
        }

        const list =
          await merchantService.searchBusinessAutoComplete(searchTerm);
        if (list?.data && list.data?.length > 0) {
          setBusinessList(list.data);
          setUserTotal(1);
        }
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchAllBusinesses],
  );

  useEffect(() => {
    // `fetchAllBusinesses` catches into `error` state, so this only fires if
    // that catch or the finally itself throws. Log rather than swallow.
    fetchAllBusinesses({}).catch(logRejection('Failed to load merchant list'));
  }, [fetchAllBusinesses]);

  // Modified handleApplyFilters to decide which API to call
  const handleApplyFilters = useCallback(() => {
    applyFilters(filtersToApply);
    setIsPageReset((prev) => !prev); // Reset pagination to the first page
  }, [applyFilters, filtersToApply]);

  // Handle change for instant data fetching
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  return {
    error,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: userTotal,
    },
    showPagination,
    filterCount,
    isPageReset,
    currentFilters, // automatically triggers fetch
    filtersToApply, // requires handleApplyFilters to trigger fetch
    clearFilters: cleanFilters,
    selectAppliedFilters, // replaces handleDateChange
    handleApplyFilters,
    selectCurrentFilters, // replaces handleFilterChange
    fetchAllBusinesses,
    businessList,
    handleBusinessAutoComplete,
  };
};

export default useMerchantList;
